import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '../lib/cn.js';
import { searchOptions } from '../lib/option-search.js';
import { Input } from './input.js';
import { OptionListbox } from './option-listbox.js';
import { Popover } from './popover.js';
import { TagInput } from './tag-input.js';
const DEFAULT_RENDER_LIMIT = 300;
/**
 * С какого числа вариантов одиночный выпадающий список получает поиск: до
 * десяти глазом находится сразу, дальше — уже листание.
 */
export const SEARCHABLE_SELECT_THRESHOLD = 10;
/** Нужен ли списку из `count` вариантов поиск (`searchable` — явное решение вызывающего). */
export function shouldSearchSelect(count, searchable) {
    return searchable ?? count > SEARCHABLE_SELECT_THRESHOLD;
}
function limitNote(shown, total) {
    return total > shown ? `Показаны первые ${shown} из ${total} — уточните поиск` : undefined;
}
function OptionContent({ option }) {
    return (_jsxs("span", { className: "flex min-w-0 items-center gap-2", children: [option.icon != null ? _jsx("span", { className: "inline-flex shrink-0", children: option.icon }) : null, _jsxs("span", { className: "flex min-w-0 flex-col", children: [_jsx("span", { className: "truncate", children: option.label }), option.description ? (_jsx("span", { className: "truncate text-xs text-[var(--color-text-muted)]", children: option.description })) : null] })] }));
}
/**
 * Выбор из СТАТИЧЕСКОГО списка с поиском — общий вид всех выпадающих списков
 * админки (эталон — выбор стран): выбранное — чипы ПРЯМО В СТРОКЕ ввода
 * (переносятся на новые строки), ввод фильтрует список (без регистра и
 * диакритики, по подписи, коду/значению и ключевым словам), клик или Enter
 * выбирают, Backspace на пустом поле снимает последний чип, × на чипе — его,
 * «Очистить» — всё. ↑/↓ ходят по списку и докручивают его к подсвеченному.
 *
 * `multiple` — множественный выбор поверх `TagInput` (тот же вид и поведение,
 * что у `CountryPicker`); без него — одиночный: в поле подпись выбранного,
 * ввод ищет, выбор закрывает список.
 *
 * Для справочника, который ищет СЕРВЕР (теги, интересы Meta), — `TagInput`
 * напрямую: там фильтрует родитель.
 */
export function Combobox(props) {
    return props.multiple ? _jsx(MultipleCombobox, { ...props }) : _jsx(SingleCombobox, { ...props });
}
Combobox.displayName = 'Combobox';
function useOptionIndex(options) {
    return React.useMemo(() => new Map(options.map((o) => [o.value, o])), [options]);
}
function MultipleCombobox({ options, value, onChange, max, clearable = true, renderChip, renderOption, renderLimit = DEFAULT_RENDER_LIMIT, getMissingLabel, placeholder = 'Найти…', emptyLabel, disabled = false, id, 'aria-label': ariaLabel = 'Выбор', 'aria-labelledby': ariaLabelledBy, 'aria-describedby': ariaDescribedBy, 'aria-invalid': ariaInvalid, onBlur, inputRef, className, }) {
    const [query, setQuery] = React.useState('');
    const byValue = useOptionIndex(options);
    const matches = React.useMemo(() => {
        const chosen = new Set(value);
        return searchOptions(options, query).filter((o) => !chosen.has(o.value));
    }, [options, query, value]);
    const shown = matches.slice(0, renderLimit);
    const labelOf = (v) => byValue.get(v)?.label ?? getMissingLabel?.(v) ?? v;
    const footer = limitNote(shown.length, matches.length);
    return (_jsx(TagInput, { identity: "id", allowCreate: false, multiselectable: true, indicator: true, value: [...value], getLabel: labelOf, renderValue: (v) => {
            const o = byValue.get(v);
            if (!o)
                return labelOf(v);
            if (renderChip)
                return renderChip(o);
            return o.icon != null ? (_jsxs("span", { className: "inline-flex min-w-0 items-center gap-1", children: [o.icon, _jsx("span", { className: "truncate", children: o.label })] })) : (o.label);
        }, renderSuggestion: (s) => {
            const o = byValue.get(s.id);
            if (!o)
                return _jsx("span", { className: "truncate", children: s.name });
            return renderOption ? renderOption(o) : _jsx(OptionContent, { option: o });
        }, suggestions: shown.map((o) => ({
            id: o.value,
            name: o.label,
            ...(o.disabled ? { disabled: true } : {}),
        })), query: query, onQueryChange: setQuery, onChange: onChange, ...(clearable ? { onClear: () => onChange([]) } : {}), maxTags: max ?? Number.MAX_SAFE_INTEGER, maxLabel: (n) => `Можно выбрать не больше ${n}`, removeLabel: (label) => `Убрать ${label}`, emptyLabel: emptyLabel ?? (options.length === 0 ? 'Нет вариантов' : 'Ничего не найдено'), ...(footer ? { listFooter: footer } : {}), disabled: disabled, placeholder: placeholder, "aria-label": ariaLabel, ...(id ? { id } : {}), ...(ariaLabelledBy ? { 'aria-labelledby': ariaLabelledBy } : {}), ...(ariaDescribedBy ? { 'aria-describedby': ariaDescribedBy } : {}), ...(ariaInvalid ? { 'aria-invalid': ariaInvalid } : {}), ...(onBlur ? { onBlur } : {}), ...(inputRef ? { inputRef } : {}), ...(className ? { className } : {}) }));
}
function SingleCombobox({ options, value, onChange, clearable = false, size = 'md', loading = false, 'data-testid': testId, renderOption, renderLimit = DEFAULT_RENDER_LIMIT, getMissingLabel, placeholder = 'Выберите…', emptyLabel, disabled = false, id, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, 'aria-describedby': ariaDescribedBy, 'aria-invalid': ariaInvalid, onBlur, inputRef, className, }) {
    const [open, setOpen] = React.useState(false);
    /** Набранный запрос; `null` — пользователь не печатает, в поле подпись выбранного. */
    const [query, setQuery] = React.useState(null);
    const [highlight, setHighlight] = React.useState(-1);
    const fieldRef = React.useRef(null);
    const ownInputRef = React.useRef(null);
    const baseId = React.useId();
    const listboxId = `${baseId}-listbox`;
    const optionId = (index) => `${baseId}-option-${index}`;
    const byValue = useOptionIndex(options);
    const setInputRef = React.useCallback((el) => {
        ownInputRef.current = el;
        if (typeof inputRef === 'function')
            inputRef(el);
        else if (inputRef)
            inputRef.current = el;
    }, [inputRef]);
    const hasValue = value != null && value !== '';
    const selected = hasValue ? byValue.get(value) : undefined;
    // Значения нет среди вариантов: подпись — от вызывающего, иначе поле пустое
    // (сырой id оператору ничего не говорит). Пока варианты грузятся — «Загрузка…».
    const missingLabel = hasValue && !selected ? getMissingLabel?.(value) : undefined;
    const selectedLabel = selected?.label ?? missingLabel ?? '';
    const waitingForOptions = hasValue && !selected && missingLabel == null && (loading || options.length === 0);
    const fieldPlaceholder = waitingForOptions
        ? 'Загрузка…'
        : selectedLabel !== ''
            ? selectedLabel
            : placeholder;
    /**
     * Доступное имя списка: явный `aria-labelledby`, иначе `aria-label`, иначе
     * `<label>` поля (`htmlFor`) — список называется так же, как поле.
     */
    function listNameProps() {
        if (ariaLabelledBy)
            return { 'aria-labelledby': ariaLabelledBy };
        if (ariaLabel)
            return { 'aria-label': ariaLabel };
        const label = ownInputRef.current?.labels?.[0];
        if (label) {
            if (!label.id)
                label.id = `${baseId}-label`;
            return { 'aria-labelledby': label.id };
        }
        return {};
    }
    // Пока не печатают — весь список (как у обычного Select), с подсветкой выбранного.
    const matches = React.useMemo(() => (query == null ? [...options] : searchOptions(options, query)), [options, query]);
    const shown = matches.slice(0, renderLimit);
    const isOpen = open && !disabled;
    const enabledIndexes = shown.flatMap((o, i) => (o.disabled ? [] : [i]));
    const navigable = isOpen && enabledIndexes.length > 0;
    const active = navigable
        ? enabledIndexes.includes(highlight)
            ? highlight
            : enabledIndexes[0]
        : -1;
    function initialHighlight(list) {
        const idx = hasValue ? list.findIndex((o) => o.value === value && !o.disabled) : -1;
        return idx >= 0 ? idx : list.findIndex((o) => !o.disabled);
    }
    function openList() {
        if (disabled || open)
            return;
        setOpen(true);
        setHighlight(initialHighlight(shown));
    }
    function close() {
        setOpen(false);
        setQuery(null);
    }
    function choose(option) {
        if (option.disabled)
            return;
        if (option.value !== value)
            onChange(option.value);
        close();
    }
    function move(step) {
        if (enabledIndexes.length === 0)
            return;
        const pos = enabledIndexes.indexOf(active);
        const next = pos < 0
            ? step === 1
                ? 0
                : enabledIndexes.length - 1
            : (pos + step + enabledIndexes.length) % enabledIndexes.length;
        setHighlight(enabledIndexes[next]);
    }
    function handleKeyDown(event) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (!isOpen) {
                openList();
                return;
            }
            move(event.key === 'ArrowDown' ? 1 : -1);
            return;
        }
        if (event.key === 'Enter') {
            // Закрытый список Enter не трогает — чужая форма должна отправиться.
            if (active < 0)
                return;
            event.preventDefault();
            choose(shown[active]);
            return;
        }
        if (event.key === 'Escape') {
            if (isOpen || query != null) {
                // Не даём Esc закрыть диалог, в котором живёт поле: сначала — список.
                event.preventDefault();
                event.stopPropagation();
                close();
            }
        }
    }
    const footer = limitNote(shown.length, matches.length);
    const showClear = clearable && hasValue && !disabled;
    return (_jsx("div", { className: cn('flex flex-col', className), children: _jsxs(Popover, { open: isOpen, onOpenChange: (next) => {
                if (!next)
                    close();
            }, children: [_jsx(Popover.Anchor, { asChild: true, children: _jsxs("div", { ref: fieldRef, "data-testid": testId, onMouseDown: (event) => {
                            if (disabled)
                                return;
                            const target = event.target;
                            if (!fieldRef.current?.contains(target))
                                return;
                            if (target.closest('button'))
                                return;
                            if (target !== ownInputRef.current) {
                                event.preventDefault();
                                ownInputRef.current?.focus();
                            }
                            if (isOpen && target !== ownInputRef.current)
                                close();
                            else
                                openList();
                        }, className: cn('flex w-full items-center gap-1.5 rounded-[var(--radius-2)] border border-[var(--color-border-default)] bg-[var(--color-surface-canvas)] px-2', size === 'sm' ? 'min-h-8' : 'min-h-10', 'focus-within:ring-2 focus-within:ring-[var(--color-border-focus)]', ariaInvalid && 'border-[var(--color-state-danger-fg)]', disabled && 'cursor-not-allowed opacity-50'), children: [query == null && selected?.icon != null ? (_jsx("span", { className: "inline-flex shrink-0", children: selected.icon })) : null, _jsx(Input, { ref: setInputRef, id: id, role: "combobox", "aria-label": ariaLabelledBy ? undefined : ariaLabel, "aria-labelledby": ariaLabelledBy, "aria-describedby": ariaDescribedBy, "aria-invalid": ariaInvalid, "aria-expanded": isOpen, "aria-haspopup": "listbox", "aria-autocomplete": "list", "aria-controls": navigable ? listboxId : undefined, "aria-activedescendant": active >= 0 ? optionId(active) : undefined, autoComplete: "off", value: query ?? selectedLabel, placeholder: fieldPlaceholder, disabled: disabled, onChange: (event) => {
                                    setQuery(event.target.value);
                                    setHighlight(-1);
                                    setOpen(true);
                                }, 
                                // Фокус выделяет подпись: следующая буква заменяет её запросом.
                                onFocus: (event) => event.currentTarget.select(), onKeyDown: handleKeyDown, onBlur: () => {
                                    if (!open)
                                        setQuery(null);
                                    onBlur?.();
                                }, className: cn('min-w-0 flex-1 border-0 bg-transparent px-1 focus-visible:ring-0', size === 'sm' ? 'h-7' : 'h-9') }), showClear ? (_jsx("button", { type: "button", "aria-label": "\u041E\u0447\u0438\u0441\u0442\u0438\u0442\u044C", title: "\u041E\u0447\u0438\u0441\u0442\u0438\u0442\u044C", onClick: () => {
                                    onChange(null);
                                    setQuery(null);
                                    ownInputRef.current?.focus();
                                }, className: "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-[var(--radius-2)] text-[var(--color-text-muted)] hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-text-primary)]", children: _jsx(X, { "aria-hidden": true, className: "h-3.5 w-3.5" }) })) : null, _jsx(ChevronDown, { "aria-hidden": true, className: cn('h-4 w-4 shrink-0 text-[var(--color-text-muted)] transition-transform', isOpen && 'rotate-180') })] }) }), _jsxs(Popover.Content, { align: "start", onOpenAutoFocus: (event) => event.preventDefault(), onCloseAutoFocus: (event) => event.preventDefault(), onInteractOutside: (event) => {
                        if (fieldRef.current?.contains(event.target))
                            event.preventDefault();
                    }, className: "w-[var(--radix-popover-trigger-width)] min-w-56 p-1", children: [shown.length === 0 ? (_jsx("div", { className: "px-2 py-1.5 text-sm text-[var(--color-text-muted)]", children: loading
                                ? 'Загрузка…'
                                : (emptyLabel ?? (options.length === 0 ? 'Нет вариантов' : 'Ничего не найдено')) })) : (_jsx(OptionListbox, { id: listboxId, ...listNameProps(), activeOptionId: active >= 0 ? optionId(active) : undefined, children: shown.map((option, index) => {
                                const isSelected = hasValue && option.value === value;
                                return (_jsxs("li", { id: optionId(index), role: "option", "aria-selected": isSelected, "aria-disabled": option.disabled ? true : undefined, "data-highlighted": index === active ? '' : undefined, ...(option.title ? { title: option.title } : {}), onMouseDown: (event) => event.preventDefault(), onMouseEnter: () => {
                                        if (!option.disabled)
                                            setHighlight(index);
                                    }, onClick: () => choose(option), className: cn('flex items-center justify-between gap-2 rounded-[var(--radius-2)] px-2 py-1.5 text-sm', option.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer', index === active
                                        ? 'bg-[var(--color-surface-sunken)] text-[var(--color-text-primary)]'
                                        : 'text-[var(--color-text-secondary)]'), children: [renderOption ? renderOption(option) : _jsx(OptionContent, { option: option }), isSelected ? (_jsx(Check, { "aria-hidden": true, className: "h-4 w-4 shrink-0 text-[var(--color-text-primary)]" })) : null] }, option.value));
                            }) })), footer ? (_jsx("div", { className: "border-t border-[var(--color-border-subtle)] px-2 pt-1.5 pb-1 text-xs text-[var(--color-text-muted)]", children: footer })) : null] })] }) }));
}
//# sourceMappingURL=combobox.js.map